import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FiltersGroup } from '@/modules/common/components/FiltersGroup.tsx';
import RegisterClassesSelectPaginate from '@/modules/common/components/RegisterClassesSelectPaginate.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { Filters } from '@/modules/issuer-dashboard/components/certificates/types.ts';
import { SelectHolder } from '@/modules/issuer-dashboard/components/transactions/SelectHolder.tsx';
import { FilterItem, Spinner, getSelectOptionByEnum } from 'ui';
import { ShareCertificateStatus } from 'api';

interface CertificateFiltersPanelProps {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  isFetching: boolean;
  hideChooseRegisterClass?: boolean;
}

export const CertificateFiltersPanel = ({
  filters,
  setFilters,
  isFetching,
  hideChooseRegisterClass = false,
}: CertificateFiltersPanelProps) => {
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();

  const handleChangeStatus = useCallback(
    (status: ShareCertificateStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const dateValue =
    filters.from_date && filters.to_date
      ? ([dayjs(filters.from_date), dayjs(filters.to_date)] as [Dayjs, Dayjs])
      : undefined;

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              from_date: date[0].format('YYYY-MM-DD'),
              to_date: date[1].format('YYYY-MM-DD'),
            }
          : omit(filters, ['from_date', 'to_date']);
      });
    },
    [setFilters],
  );

  const handleChangeHolder = useCallback(
    (shareholder_id?: number) => {
      setFilters(filters => {
        return shareholder_id
          ? {
              ...filters,
              shareholder_id: +shareholder_id,
            }
          : omit(filters, ['shareholder_id']);
      });
    },
    [setFilters],
  );

  const handleChangeRegisterClass = useCallback(
    (registerClassId: number | null) => {
      setFilters(filters => {
        return registerClassId
          ? {
              ...filters,
              register_class_id: +registerClassId,
            }
          : omit(filters, ['register_class_id']);
      });
    },
    [setFilters],
  );

  return (
    <FiltersGroup variant="inline" className="flex w-full flex-wrap items-center gap-2">
      <FilterItem
        placeholder="Date"
        type="date"
        onChange={handleChangeDate}
        value={dateValue}
        className="rounded-lg"
      />

      {!hideChooseRegisterClass && (
        <RegisterClassesSelectPaginate
          registerId={+registerId}
          onChange={item => {
            handleChangeRegisterClass(item?.value.id || null);
          }}
          value={filters?.register_class_id}
          isMulti={false}
          placeholder="Class"
          label=""
          className="flex w-full items-center gap-2 rounded-lg border px-2 font-[Inter] text-sm leading-[22px] min-w-[200px] max-w-[350px]"
          classNameSelect="w-full"
          formFieldTitle=""
          showBorderBottom={false}
          withBorder={false}
          clearable={true}
        />
      )}

      <SelectHolder
        setShareholder={handleChangeHolder}
        shareholderId={filters.shareholder_id}
        registerId={registerId}
        placeholder="Holder"
        label=""
        isClearable={true}
        className="min-w-[200px] max-w-[250px]"
      />

      <FilterItem
        placeholder="Status"
        type="select"
        value={filters.status}
        options={getSelectOptionByEnum<ShareCertificateStatus>(ShareCertificateStatus)}
        onChange={handleChangeStatus}
        className="rounded-lg"
        classes={{
          label: 'w-[85px] shrink-0',
          element: 'w-full min-w-[140px] [&>div>div]:gap-1 [&>div>div]:md:!gap-3',
        }}
      />

      {isFetching && <Spinner size="md" variant="primary" />}
    </FiltersGroup>
  );
};
